import React, { useState } from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ManageFeePaymentsPage from './ManageFeePaymentsPage';
import PaymentProcessingPage from './PaymentProcessingPage';

/**
 * FEES — one page for the whole of a bursar's day, merging what used to be two.
 *
 * Manage Fee Payments and Payment Processing asked different questions of the
 * same data: one organised by STUDENT answering "who owes what", the other by
 * PAYMENT answering "what money moved". A bursar crosses between them
 * constantly — see who owes, take the money, approve the receipt the parent
 * sent, print it — and two pages meant leaving the task halfway to do the next
 * part of it. Nothing forced them apart: identical permissions, so it was never
 * an access split, only how the page happened to grow.
 *
 * COMPOSED RATHER THAN REWRITTEN. Both existing pages are large and carry
 * behaviour worth keeping exactly as it is — the instalment waterfall, the
 * grouping of a parent's submission by its proof, the gateway paths. Rewriting
 * them into a single component would have risked all of it for a layout change.
 * Each keeps its own data loading; only the navigation is unified.
 *
 * The consequence, stated rather than hidden: the two halves fetch
 * independently, so switching tabs refetches rather than sharing a cache. That
 * is the price of not rewriting two working pages, and it is the right trade
 * while the underlying behaviour is still being corrected.
 *
 * Both /manage-fee-payments and /payment-processing render this, so a role
 * granted only one of them still reaches the whole page.
 */
export default function FeesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('owed');

  return (
    <Box>
      {/* THE TABS ARE THE HEADING. They used to be a thin strip of text tabs
          with each half's own <h1> shouting directly beneath them — and those
          headings still carried the OLD page names, so the loudest words on
          screen contradicted the control that switched between them.

          Full width, sized like a heading, with the old titles removed below,
          so the tab a bursar is on IS the title of what they are looking at. */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: '1.05rem',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 60,
            },
            '& .MuiTabs-indicator': { height: 3 },
          }}
        >
          <Tab value="owed" label={t('payments.tabStatus', 'Manage Payments')} />
          <Tab value="money" label={t('payments.tabHistory', 'Process Payments')} />
        </Tabs>
      </Paper>

      {/* Kept mounted-on-demand rather than both at once: each loads a class's
          students, structures and payments, and mounting both would double
          every request on arrival for a tab the user may not open. */}
      {tab === 'owed' ? <ManageFeePaymentsPage /> : <PaymentProcessingPage />}
    </Box>
  );
}
